import { Suspense, lazy } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { URLS } from "./utils/constants";
import ProtectedRoute from "./components/common/ProtectedRoute";
import LoginProtectedRoute from "./components/common/LoginProtectedRoute";
import ScrollPersistence from "./components/common/ScrollPersistence";
import MetaPixelTracker from "./components/common/MetaPixelTracker";
import { ToastProvider } from "./hooks/useToast";

import { ThemeProvider } from "./context/ThemeContext";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

const muiTheme = createTheme({
  typography: {
    fontFamily: [
      '"Plus Jakarta Sans"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Oxygen"',
      '"Ubuntu"',
      '"Cantarell"',
      '"Fira Sans"',
      '"Droid Sans"',
      '"Helvetica Neue"',
      'sans-serif',
    ].join(','),
  },
});

const Layout = lazy(() => import("./components/layout/MainLayout/MainLayout"));
const UserLayout = lazy(() => import("./components/layout/AdminLayout/AdminLayout"));
const LandingPage = lazy(() => import("./views/Landing/Landing"));
const PrivacyPolicy = lazy(() => import("./views/Landing/Pages/PrivacyPolicy"));
const AdminDashboard = lazy(() => import("./views/Admin/Dashboard/AdminDashboard"));
const Members = lazy(() => import("./views/Admin/Members/Members"));
const Trainers = lazy(() => import("./views/Admin/Trainers/Trainers"));
const UserDashboard = lazy(() => import("./views/User/UserDashboard"));
const TrainerDashboard = lazy(() => import("./views/Trainer/TrainerDashboard"));
const PricePackage = lazy(() => import("./views/Admin/PricePackage/PricePackage"));
const PremiumMembers = lazy(() => import("./views/Admin/PremiumMembers/PremiumMemeber"));
const MusicManagement = lazy(() => import("./views/Admin/Music/MusicManagement"));
const LiveRoom = lazy(() => import("./views/Room/LiveRoom"));
const SharedClassPage = lazy(() => import("./views/Share/SharedClassPage"));
const PTBookingPage = lazy(() => import("./views/User/PTBookingPage"));

const PremiumPageLayout = lazy(() => import("./views/Landing/Pages/PremiumPageLayout"));
const HowItWorksPage = lazy(() => import("./views/Landing/Pages/HowItWorksPage"));
const ProgramsPage = lazy(() => import("./views/Landing/Pages/ProgramsPage"));
const CoachesPage = lazy(() => import("./views/Landing/Pages/CoachesPage"));
const Blog = lazy(() => import("./views/Landing/Pages/Blog"));
const FAQ = lazy(() => import("./views/Landing/sections/Premium/FAQ"));

function App() {
  return (
    <>
      <ToastProvider>
        <ThemeProvider>
          <MuiThemeProvider theme={muiTheme}>
            <Router>
              <MetaPixelTracker />
              <ScrollPersistence />
              <Routes>
                <Route
                  path={URLS.SHARE}
                  element={
                    <Suspense fallback={null}><SharedClassPage /></Suspense>
                  }
                />
                
                {/* Premium Standalone Pages */}
                <Route
                  path="/how-ReAlign-works"
                  element={
                    <Suspense fallback={null}>
                      <PremiumPageLayout>
                        <HowItWorksPage />
                      </PremiumPageLayout>
                    </Suspense>
                  }
                />
                <Route
                  path="/programs"
                  element={
                    <Suspense fallback={null}>
                      <PremiumPageLayout>
                        <ProgramsPage />
                      </PremiumPageLayout>
                    </Suspense>
                  }
                />
                <Route
                  path="/coaches"
                  element={
                    <Suspense fallback={null}>
                      <PremiumPageLayout>
                        <CoachesPage />
                      </PremiumPageLayout>
                    </Suspense>
                  }
                />
                <Route
                  path="/Blogs"
                  element={
                    <Suspense fallback={null}>
                      <PremiumPageLayout>
                        <Blog />
                      </PremiumPageLayout>
                    </Suspense>
                  }
                />
                <Route
                  path="/faq"
                  element={
                    <Suspense fallback={null}>
                      <PremiumPageLayout>
                        <FAQ />
                      </PremiumPageLayout>
                    </Suspense>
                  }
                />

                <Route element={<Suspense fallback={null}><Layout /></Suspense>}>
                  <Route index element={<Navigate to={URLS.HOME} replace />} />
                  <Route
                    path={URLS.HOME}
                    element={
                      <LoginProtectedRoute>
                        <Suspense fallback={null}><LandingPage /></Suspense>
                      </LoginProtectedRoute>
                    }
                  />
                  <Route
                    path="/privacy-policy"
                    element={
                      <Suspense fallback={null}><PrivacyPolicy /></Suspense>
                    }
                  />
                </Route>
                {/* User Dashboard — standalone full-page (has its own TopNav + HeroSection) */}
                <Route
                  path={URLS.USERS}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={null}><UserDashboard /></Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={null}><UserDashboard /></Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/room/:classId"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={null}><LiveRoom /></Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pt/book"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={null}><PTBookingPage /></Suspense>
                    </ProtectedRoute>
                  }
                />
                {/* Admin / Trainer routes — wrapped in UserLayout (sidebar + header) */}
                <Route element={<Suspense fallback={null}><UserLayout /></Suspense>}>
                  <Route
                    path={URLS.ADMIN}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={null}><AdminDashboard /></Suspense>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path={URLS.PRICE_PACKAGE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={null}><PricePackage /></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={URLS.MEMBERS}
                    element={
                      <ProtectedRoute allowedRoles={["ADMIN"]}>
                        <Suspense fallback={null}><Members /></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={URLS.TRAINERS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={null}><Trainers /></Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={URLS.TRAINERS_DASHBOARD}
                    element={
                      <ProtectedRoute><Suspense fallback={null}><TrainerDashboard /></Suspense></ProtectedRoute>
                    }
                  />
                  <Route
                    path={URLS.PREMIUM_MEMBERS}
                    element={
                      <ProtectedRoute><Suspense fallback={null}><PremiumMembers /></Suspense></ProtectedRoute>
                    }
                  />
                  <Route
                    path={URLS.MUSIC}
                    element={
                      <ProtectedRoute allowedRoles={["ADMIN"]}>
                        <Suspense fallback={null}><MusicManagement /></Suspense>
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Routes>
            </Router>
          </MuiThemeProvider>
        </ThemeProvider>
      </ToastProvider>
    </>
  );
}

export default App;
